import { create } from 'zustand';
import * as notificationService from '../services/notificationService';
import notificationSoundService from '../services/notificationSoundService';

const useNotificationStore = create((set, get) => ({
  // State
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  
  // Snackbar state for UI notifications
  snackbar: {
    open: false,
    message: '',
    severity: 'info', // 'success', 'info', 'warning', 'error'
    autoHideDuration: 5000, // 5 seconds
  },
  
  // Stack of notifications for multiple display
  notificationStack: [],
  
  // Actions
  fetchNotifications: async (page = 1, limit = 10) => {
    set({ isLoading: true, error: null });
    try {
      const response = await notificationService.getNotifications(page, limit);
      set({ 
        notifications: response.data.notifications,
        isLoading: false
      });
    } catch (error) {
      set({ 
        isLoading: false,
        error: error.message || 'Failed to fetch notifications' 
      });
    }
  },
  
  fetchUnreadCount: async () => {
    try {
      console.log('Fetching unread notification count...');
      const response = await notificationService.getUnreadCount();
      console.log('Unread count response:', response.data);
      set({ unreadCount: response.data.count });
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  },
  
  markAsRead: async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      
      // Update local state
      set(state => ({
        notifications: state.notifications.map(notification =>
          notification._id === notificationId
            ? { ...notification, isRead: true }
            : notification
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
      
      return true;
    } catch (error) {
      console.log('Failed to mark notification as read:', error);
      return false;
    }
  },
  
  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      
      // Update local state
      set(state => ({
        notifications: state.notifications.map(notification => ({
          ...notification,
          isRead: true
        })),
        unreadCount: 0
      }));
      
      return true;
    } catch (error) {
      console.log('Failed to mark all notifications as read:', error);
      return false;
    }
  },
  
  deleteNotification: async (notificationId) => {
    try {
      await notificationService.deleteNotification(notificationId);
      
      // Update local state
      const { notifications, unreadCount } = get();
      const notification = notifications.find(n => n._id === notificationId);
      
      set({
        notifications: notifications.filter(n => n._id !== notificationId),
        unreadCount: notification && !notification.isRead 
          ? Math.max(0, unreadCount - 1) 
          : unreadCount
      });
      
      return true;
    } catch (error) {
      console.log('Failed to delete notification:', error);
      return false;
    }
  },
  
  // Snackbar actions for UI notifications
  showSnackbar: (message, severity = 'info', duration = 5000) => {
    set({
      snackbar: {
        open: true,
        message,
        severity,
        autoHideDuration: duration,
      }
    });
  },
  
  closeSnackbar: () => {
    set(state => ({
      snackbar: {
        ...state.snackbar,
        open: false,
      }
    }));
  },
  
  // Helper methods for common notifications
  showSuccess: (message, duration = 5000) => {
    get().showNotification({ message, type: 'success', duration });
    // Play success sound if enabled
    if (notificationSoundService.isEnabled()) {
      // notificationSoundService.playSound('success');
    }
  },

  showError: (message, duration = 5000) => {
    get().showNotification({ message, type: 'error', duration });
    // Play error sound if enabled
    if (notificationSoundService.isEnabled()) {
      // notificationSoundService.playSound('error');
    }
  },

  showInfo: (message, duration = 5000) => {
    get().showNotification({ message, type: 'info', duration });
    // Play info sound if enabled
    if (notificationSoundService.isEnabled()) {
      // notificationSoundService.playSound('info');
    }
  },

  showWarning: (message, duration = 5000) => {
    get().showNotification({ message, type: 'warning', duration });
    // Play warning sound if enabled
    if (notificationSoundService.isEnabled()) {
      // notificationSoundService.playSound('warning');
    }
  },
  
  // Enhanced methods with title support
  showNotification: ({ message, type = 'info', title, duration = 5000 }) => {
    const fullMessage = title ? `${title}\n${message}` : message;
    const severity = type === 'error' ? 'error' : 
                    type === 'success' ? 'success' : 
                    type === 'warning' ? 'warning' : 'info';
    
    // Add to notification stack
    const id = Date.now() + Math.random();
    set(state => ({
      notificationStack: [...state.notificationStack, {
        id,
        message: fullMessage,
        severity,
        autoHideDuration: duration,
        timestamp: new Date()
      }]
    }));
    
    // Also update legacy snackbar for backward compatibility
    get().showSnackbar(fullMessage, severity, duration);
  },
  
  // Remove notification from stack
  removeFromStack: (id) => {
    set(state => ({
      notificationStack: state.notificationStack.filter(n => n.id !== id)
    }));
  },
  
  // Clear all notifications
  clearAllSnackbars: () => {
    set({ notificationStack: [] });
  },
}));

export default useNotificationStore;